import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import type { InviteOffer } from '../api/types'
import { Button, ErrorNote, LoadingBlock, Logo } from '../components/ui'
import { useMe } from '../hooks/useMe'
import { useAcceptInvite, useInviteOffer } from '../hooks/useWorkspaceData'
import { ROLE_LABELS } from './WorkspaceSettingsPage'

const WHAT: Record<string, string> = {
  owner: 'run everything, billing and the workspace settings included',
  editor: 'upload recordings, edit transcripts and text, make clips and publish episodes',
  viewer: 'read the shows, episodes and transcripts, without changing anything',
}

function blockedMessage(offer: InviteOffer, signedInAs: string | undefined): string {
  switch (offer.blocked) {
    case 'expired':
      return 'This invitation has expired. Ask whoever sent it for a new one.'
    case 'accepted':
      return 'This invitation has already been used.'
    case 'revoked':
      return 'This invitation was cancelled.'
    case 'wrong_account':
      return `It was sent to ${offer.email}, and you are signed in as ${signedInAs}. Sign out and back in as ${offer.email} to accept it.`
    default:
      return 'This invitation cannot be accepted.'
  }
}

/** Where an invitation email lands. Readable signed out; accepting needs the
 *  account it was sent to. */
export function InvitePage() {
  const { token = '' } = useParams()
  const me = useMe()
  const offer = useInviteOffer(token)
  const accept = useAcceptInvite(token)
  const navigate = useNavigate()

  if (offer.isPending || me.isPending) return <LoadingBlock />
  if (offer.isError) {
    return (
      <Frame>
        <h1>Invitation not found</h1>
        <p className="muted">This link is wrong or was never an invitation. Ask whoever invited you to send it again.</p>
        <Link to="/studio">Open your studio</Link>
      </Frame>
    )
  }

  const invite = offer.data
  const signedIn = me.data?.authenticated === true
  const signedInAs = me.data?.user?.email
  if (invite.already_a_member) return <Navigate to="/studio" replace />

  return (
    <Frame>
      <h1>Join {invite.workspace_name}</h1>
      <p className="muted">
        {invite.invited_by_email ? (
          <><strong>{invite.invited_by_email}</strong> invited you to {invite.workspace_name} on indiefuture</>
        ) : (
          <>You have been invited to {invite.workspace_name} on indiefuture</>
        )}{' '}
        as <strong>{ROLE_LABELS[invite.role]}</strong>, so you can {WHAT[invite.role]}.
      </p>
      {!invite.open ? (
        <ErrorNote message={blockedMessage(invite, signedInAs)} />
      ) : signedIn ? (
        <>
          {accept.error && <ErrorNote error={accept.error} />}
          <Button
            block
            busy={accept.isPending}
            onClick={() => accept.mutate(undefined, { onSuccess: () => navigate('/studio') })}
          >
            Join {invite.workspace_name}
          </Button>
          <p className="muted small">You are signed in as {signedInAs}. You keep your own workspace as well.</p>
        </>
      ) : (
        <>
          <Link className="btn btn-primary btn-block" to={`/login?return_to=${encodeURIComponent(`/invites/${token}`)}`}>
            Sign in as {invite.email}
          </Link>
          <p className="muted small">The invitation works only for {invite.email}.</p>
        </>
      )}
    </Frame>
  )
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="auth-card card">
        <div className="card-body stack">
          <div className="brand brand-lg"><Logo size={28} /> indiefuture</div>
          {children}
        </div>
      </div>
    </div>
  )
}
